import React from "react";
import {
  Calendar,
  Users,
  MapPin,
  Clock,
  User,
  FileText,
  Target,
  CheckCircle,
  XCircle,
  AlertCircle,
  Mail,
  Building,
  Briefcase,
  DollarSign,
  GraduationCap,
  Star,
  Download,
  Phone,
  MapPin as LocationIcon,
  Edit,
  Trash2,
  MessageSquare,
  RotateCcw,
  ExternalLink,
} from "lucide-react";
import DOMPurify from "dompurify";
import { FaRupeeSign } from "react-icons/fa";
import StatusBadge from "../../components/UI/StatusBadge";

const HiringRequestDetails = ({
  hiringRequest,
  hiring_request_id,
  close,
  user,
  roles = [],
  onEdit,
  onDelete,
  onFeedback,
  onRevert,
  onConvertToCampaign,
}) => {
  if (!hiringRequest) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
        <div className="text-center">
          <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <p className="text-gray-500 text-lg">No hiring request selected</p>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return "Not specified";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="mx-auto bg-white shadow-2xl overflow-hidden">
      {/* Header Section */}
      <div className="bg-blue-500 px-8 py-8 text-white font-poppins">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h1 className="text-3xl font-semibold mb-2">
              {hiringRequest.designation || hiringRequest.position}
            </h1>
            <div className="flex items-center space-x-6 text-blue-100">
              <div className="flex items-center space-x-2">
                <Building className="h-5 w-5" />
                <span>{hiringRequest.department}</span>
              </div>
              <div className="flex items-center space-x-2">
                <LocationIcon className="h-5 w-5" />
                <span>{hiringRequest.location}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={close}
              className="text-white dark:hover:text-gray-400 hover:bg-blue-800 hover:text-gray-200 text-xl px-2 py-1 rounded-md transition delay-100"
            >
              ✖
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-8 font-poppins">
        {/* Key Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
            <div className="flex items-center space-x-3 mb-2">
              <div className="bg-blue-500 p-2 rounded-lg">
                <Users className="h-5 w-5 text-white" />
              </div>
              <span className="text-blue-600 font-medium">Positions</span>
            </div>
            <p className="text-2xl font-semibold text-gray-900">
              {hiringRequest.number_of_positions}
            </p>
          </div>

          <div className="bg-blue-50 rounded-xl p-6 border border-green-100">
            <div className="flex items-center space-x-3 mb-2">
              <div className="bg-green-500 p-2 rounded-lg">
                <Briefcase className="h-5 w-5 text-white" />
              </div>
              <span className="text-green-600 font-medium">Employment</span>
            </div>
            <p className="text-lg font-semibold text-gray-900">
              {hiringRequest.employment_type}
            </p>
          </div>

          <div className="bg-blue-50 rounded-xl p-6 border border-purple-100">
            <div className="flex items-center space-x-3 mb-2">
              <div className="bg-purple-500 p-2 rounded-lg">
                <Target className="h-5 w-5 text-white" />
              </div>
              <span className="text-purple-600 font-medium">Hiring Type</span>
            </div>
            <p className="text-lg font-semibold text-gray-900">
              {hiringRequest.hiring_type}
            </p>
            {hiringRequest.hiring_type === "Replacement" && (
              <p className="text-sm text-gray-600">
                Employee Name : {hiringRequest.replaced_employee_name}
              </p>
            )}
          </div>

          <div className="bg-blue-50 rounded-xl p-6 border border-orange-100">
            <div className="flex items-center space-x-3 mb-2">
              <div className="bg-orange-500 p-2 rounded-lg">
                <User className="h-5 w-5 text-white" />
              </div>
              <span className="text-orange-600 font-medium">Requester</span>
            </div>
            <p className="text-lg font-semibold text-gray-900">
              {hiringRequest.requester_name}
            </p>
            <p className="text-sm text-gray-600">
              {hiringRequest.requester_official_email}
            </p>
          </div>
        </div>

        {/* Recruitment Request Status */}
        {hiringRequest.recruitment_request_status && (
          <div className="mb-8 font-poppins flex items-center space-x-5">
            <h3 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
              <CheckCircle className="h-6 w-6 text-blue-600" />
              <span>Status</span>
            </h3>
            <div className="flex items-center">
              <StatusBadge status={hiringRequest.recruitment_request_status} />
            </div>
          </div>
        )}

        {/* Skills Section */}
        {hiringRequest.skill_set && (
          <div className="mb-8 font-poppins">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
              <Target className="h-6 w-6 text-blue-600" />
              <span>Required Skills</span>
            </h3>
            <div className="flex flex-wrap gap-3">
              {hiringRequest.skill_set
                .split(/\r\n|\n|,/)
                .map((skill) => skill.trim())
                .filter((skill) => skill.length > 0)
                .map((skill, index) => (
                  <span
                    key={index}
                    className="bg-blue-50 text-blue-600 px-3 py-1 rounded-full text-sm font-medium border border-blue-100 transition-shadow duration-200"
                  >
                    {skill}
                  </span>
                ))}
            </div>
          </div>
        )}

        {/* Requester Information Section */}
        {/* <div className="mb-8">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                        <User className="h-6 w-6 text-blue-600" />
                        <span>Requester Information</span>
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                            <div className="flex items-center space-x-3 mb-3">
                                <User className="h-5 w-5 text-blue-600" />
                                <span className="font-medium text-gray-900">Requester Name</span>
                            </div>
                            <p className="text-gray-700">{hiringRequest.requester_name}</p>
                        </div>
                        <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                            <div className="flex items-center space-x-3 mb-3">
                                <Mail className="h-5 w-5 text-blue-600" />
                                <span className="font-medium text-gray-900">Official Email</span>
                            </div>
                            <p className="text-gray-700">{hiringRequest.requester_official_email}</p>
                        </div>
                    </div>
                </div> */}

        {/* Position & Employment Details */}
        <div className="mb-8 border border-gray-300 rounded-lg p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
            <Briefcase className="h-6 w-6 text-green-600" />
            <span>Position & Employment Details</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
            <div className="flex items-center">
              <div className="flex items-center space-x-3">
                <FaRupeeSign className="h-5 w-5 text-green-600" />
                <span className="font-medium text-gray-900">
                  Stipend/CTC per Month:
                </span>
              </div>
              <span className="text-gray-700 ml-4">
                {hiringRequest.stipend_ctc_per_month || "Not specified"}
              </span>
            </div>
          </div>
        </div>

        {/* Job Description */}
        {hiringRequest.job_description &&
          typeof hiringRequest.job_description === "string" &&
          !hiringRequest.job_description_url && (
            <div className="mb-8 border border-gray-300 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                <FileText className="h-6 w-6 text-green-600" />
                <span>Job Description</span>
              </h3>
              <div
                className="text-gray-700 leading-relaxed overflow-hidden"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(hiringRequest.job_description, {
                    ALLOWED_TAGS: [
                      "p",
                      "br",
                      "strong",
                      "em",
                      "u",
                      "h1",
                      "h2",
                      "h3",
                      "h4",
                      "h5",
                      "h6",
                      "ul",
                      "ol",
                      "li",
                      "blockquote",
                      "code",
                      "pre",
                      "span",
                      "div",
                    ],
                    ALLOWED_ATTR: ["class", "style"],
                  }),
                }}
              />
            </div>
          )}

        {/* Approval & Location Details */}
        {/* <div className="mb-8">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                        <CheckCircle className="h-6 w-6 text-purple-600" />
                        <span>Approval & Location Details</span>
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                            <div className="flex items-center space-x-3 mb-3">
                                <CheckCircle className="h-5 w-5 text-purple-600" />
                                <span className="font-medium text-gray-900">Approval Received</span>
                            </div>
                            <p className="text-gray-700">{hiringRequest.approval_received}</p>
                        </div>
                        {hiringRequest.approval_received === "Yes" && hiringRequest.approver_name && (
                        <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                            <div className="flex items-center space-x-3 mb-3">
                                <User className="h-5 w-5 text-purple-600" />
                                <span className="font-medium text-gray-900">Approver Name</span>
                            </div>
                            <p className="text-gray-700">{hiringRequest.approver_name}</p>
                        </div>
                        )}

                        <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                            <div className="flex items-center space-x-3 mb-3">
                                <LocationIcon className="h-5 w-5 text-purple-600" />
                                <span className="font-medium text-gray-900">Location</span>
                            </div>
                            <p className="text-gray-700">{hiringRequest.location}</p>
                        </div>
                    </div>
                </div> */}

        {/* Requirements & Skills */}
        <div className="mb-8 border border-gray-300 rounded-lg p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
            <GraduationCap className="h-6 w-6 text-orange-600" />
            <span>Requirements & Skills</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
            <div className="flex items-center">
              <div className="flex items-center space-x-3">
                <GraduationCap className="h-5 w-5 text-orange-600" />
                <span className="font-medium text-gray-900">
                  Minimum Qualification:
                </span>
              </div>
              <span className="text-gray-700 ml-4">
                {hiringRequest.minimum_qualification || "Not specified"}
              </span>
            </div>
            <div className="flex items-center">
              <div className="flex items-center space-x-3">
                <Clock className="h-5 w-5 text-orange-600" />
                <span className="font-medium text-gray-900">
                  Years Experience:
                </span>
              </div>
              <span className="text-gray-700 ml-4">
                {hiringRequest.years_experience_required
                  ? `${hiringRequest.years_experience_required} year${
                      hiringRequest.years_experience_required === 1 ? "" : "s"
                    }`
                  : "Not specified"}
              </span>
            </div>
          </div>
        </div>

        {/* Interview & Process Details */}
        <div className="mb-8 border border-gray-300 rounded-lg p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
            <Users className="h-6 w-6 text-indigo-600" />
            <span>Interview & Process Details</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
            <div className="flex items-center">
              <div className="flex items-center space-x-3">
                <Clock className="h-5 w-5 text-indigo-600" />
                <span className="font-medium text-gray-900">
                  TAT Days to Close:
                </span>
              </div>
              <span className="text-gray-700 ml-4">
                {hiringRequest.tat_days_to_close || "Not specified"}
              </span>
            </div>
            <div className="flex items-center">
              <div className="flex items-center space-x-3">
                <Users className="h-5 w-5 text-indigo-600" />
                <span className="font-medium text-gray-900">
                  Interview Rounds:
                </span>
              </div>
              <span className="text-gray-700 ml-4">
                {hiringRequest.interview_rounds}
              </span>
            </div>
          </div>

          {/* Interview Process Steps */}
          {hiringRequest.round_type_mapping && (
            <div className="mt-6">
              <div className="flex items-center space-x-3 mb-4">
                <FileText className="h-5 w-5 text-indigo-600" />
                <span className="font-medium text-gray-900">
                  Interview Process Steps:
                </span>
              </div>
              <div className="space-y-3">
                {(() => {
                  let rounds = [];
                  try {
                    // Handle both array and object formats
                    if (Array.isArray(hiringRequest.round_type_mapping)) {
                      // If it's an array, map to round numbers
                      rounds = hiringRequest.round_type_mapping.map(
                        (roundType, index) => ({
                          round_number: index + 1,
                          round_type: roundType,
                        }),
                      );
                    } else {
                      // If it's an object or JSON string
                      const mapping =
                        typeof hiringRequest.round_type_mapping === "string"
                          ? JSON.parse(hiringRequest.round_type_mapping)
                          : hiringRequest.round_type_mapping;

                      // Convert object to sorted array
                      rounds = Object.entries(mapping)
                        .sort(([a], [b]) => parseInt(a) - parseInt(b))
                        .map(([roundNumber, roundType]) => ({
                          round_number: parseInt(roundNumber),
                          round_type: roundType,
                        }));
                    }
                  } catch (error) {
                    console.error("Error parsing round_type_mapping:", error);
                    return (
                      <p className="text-gray-500 text-sm">
                        Unable to parse interview steps
                      </p>
                    );
                  }

                  if (rounds.length === 0) {
                    return (
                      <p className="text-gray-500 text-sm">
                        No interview steps configured
                      </p>
                    );
                  }

                  return rounds.map((round, index) => (
                    <div
                      key={round.round_number || index}
                      className="flex items-center space-x-3 p-3 bg-white rounded-lg border border-gray-200 hover:border-indigo-300 transition-colors"
                    >
                      <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center font-semibold text-sm">
                        {round.round_number}
                      </div>
                      <div className="flex-1">
                        <span className="text-gray-700 font-medium">
                          {round.round_type}
                        </span>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>
          )}
        </div>

        {/* Additional Notes & Suggestions */}
        {(hiringRequest.other_notes ||
          hiringRequest.additional_suggestions) && (
          <div className="mb-8 border border-gray-300 rounded-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
              <FileText className="h-6 w-6 text-yellow-600" />
              <span>Additional Notes & Suggestions</span>
            </h3>
            <div className="space-y-3">
              {hiringRequest.other_notes && (
                <div className="flex items-start">
                  <div className="flex items-center space-x-3 min-w-[200px]">
                    <FileText className="h-5 w-5 text-yellow-600" />
                    <span className="font-medium text-gray-900">
                      Other Notes:
                    </span>
                  </div>
                  <p className="text-gray-700 ml-4 whitespace-pre-wrap">
                    {hiringRequest.other_notes}
                  </p>
                </div>
              )}
              {hiringRequest.additional_suggestions && (
                <div className="flex items-start">
                  <div className="flex items-center space-x-3 min-w-[200px]">
                    <FileText className="h-5 w-5 text-yellow-600" />
                    <span className="font-medium text-gray-900">
                      Additional Suggestions:
                    </span>
                  </div>
                  <p className="text-gray-700 ml-4 whitespace-pre-wrap">
                    {hiringRequest.additional_suggestions}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* File Attachments */}
        {(hiringRequest.approval_attachment_mail_url ||
          hiringRequest.job_description_url ||
          hiringRequest.kra_kpi_url ||
          hiringRequest.technical_test_path_url) && (
          <div className="mb-8 border border-gray-300 rounded-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
              <Download className="h-6 w-6 text-red-600" />
              <span>File Attachments</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
              {hiringRequest.approval_attachment_mail_url && (
                <div className="flex items-center">
                  <div className="flex items-center space-x-3">
                    <Download className="h-5 w-5 text-red-600" />
                    <span className="font-medium text-gray-900">
                      Approval Attachment:
                    </span>
                  </div>
                  <a
                    href={hiringRequest.approval_attachment_mail_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 hover:underline hover:underline-offset-4 flex items-center gap-2 ml-4"
                  >
                    <Download className="h-4 w-4" />
                    View File
                  </a>
                </div>
              )}
              {hiringRequest.job_description_url && (
                <div className="flex items-center">
                  <div className="flex items-center space-x-3">
                    <Download className="h-5 w-5 text-red-600" />
                    <span className="font-medium text-gray-900">
                      Job Description:
                    </span>
                  </div>
                  <a
                    href={hiringRequest.job_description_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 hover:underline hover:underline-offset-4 flex items-center gap-2 ml-4"
                  >
                    <Download className="h-4 w-4" />
                    View File
                  </a>
                </div>
              )}
              {hiringRequest.kra_kpi_url && (
                <div className="flex items-center">
                  <div className="flex items-center space-x-3">
                    <Download className="h-5 w-5 text-red-600" />
                    <span className="font-medium text-gray-900">KRA/KPI:</span>
                  </div>
                  <a
                    href={hiringRequest.kra_kpi_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 hover:underline hover:underline-offset-4 flex items-center gap-2 ml-4"
                  >
                    <Download className="h-4 w-4" />
                    View File
                  </a>
                </div>
              )}
              {hiringRequest.technical_test_path_url && (
                <div className="flex items-center">
                  <div className="flex items-center space-x-3">
                    <Download className="h-5 w-5 text-red-600" />
                    <span className="font-medium text-gray-900">
                      Technical Test:
                    </span>
                  </div>
                  <a
                    href={hiringRequest.technical_test_path_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 hover:underline hover:underline-offset-4 flex items-center gap-2 ml-4"
                  >
                    <Download className="h-4 w-4" />
                    View File
                  </a>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Rating */}
        {/* <div className="mb-8">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                        <svg className="h-6 w-6 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path>
                        </svg>
                        <span>Rating</span>
                    </h3>
                    <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                        <div className="flex items-center space-x-1">
                            {hiringRequest.rating ? (
                                <>
                                    {[1, 2, 3, 4, 5].map((heart) => (
                                        <svg 
                                            key={heart}
                                            className={`w-8 h-8 ${
                                                parseInt(hiringRequest.rating) >= heart 
                                                    ? "text-red-500" 
                                                    : "text-gray-300"
                                            }`}
                                            fill={parseInt(hiringRequest.rating) >= heart ? "currentColor" : "none"}
                                            stroke="currentColor" 
                                            viewBox="0 0 24 24">
                                            <path 
                                                strokeLinecap="round" 
                                                strokeLinejoin="round" 
                                                strokeWidth="2" 
                                                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                                            />
                                        </svg>
                                    ))}
                                    <span className="ml-3 text-lg text-gray-700 font-medium">
                                        {hiringRequest.rating}/5 hearts
                                    </span>
                                </>
                            ) : (
                                <span className="text-gray-500 text-lg">Not specified</span>
                            )}
                        </div>
                    </div>
                </div> */}

        {/* Action Information */}
        {hiringRequest.actioner_remarks && (
          <div className="mb-8 border border-gray-300 rounded-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
              <svg
                className="h-6 w-6 text-purple-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                ></path>
              </svg>
              <span>Action Remarks</span>
            </h3>
            <div className="pl-4">
              <p className="text-gray-700 whitespace-pre-wrap">
                {hiringRequest.actioner_remarks}
              </p>
            </div>
          </div>
        )}

        {/* Action Buttons - Bottom Right */}
        {!(
          roles.includes("HR") &&
          hiringRequest.recruitment_request_status === "Reverted"
        ) && (
          <div className="mb-8 flex justify-end">
            <div className="flex flex-wrap gap-3">
              {/* Edit */}
              {roles.includes("Manager") &&
                user?.id === hiringRequest.requester_id &&
                (hiringRequest.recruitment_request_status === "Pending" ||
                  hiringRequest.recruitment_request_status === "Reverted") &&
                onEdit && (
                  <button
                    onClick={() => onEdit(hiringRequest)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    <Edit className="h-4 w-4" />
                    Edit
                  </button>
                )}

              {/* Delete */}
              {roles.includes("Manager") &&
                hiringRequest.status !== "Approved" &&
                user?.id === hiringRequest.requester_id &&
                (hiringRequest.recruitment_request_status === "Pending" ||
                  hiringRequest.recruitment_request_status === "Reverted") &&
                onDelete && (
                  <button
                    onClick={() => onDelete(hiringRequest.id)}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                    Cancel
                  </button>
                )}

              {/* Feedback */}
              {hiringRequest.recruitment_request_status === "Reverted" &&
                !roles.includes("HR") &&
                onFeedback && (
                  <button
                    onClick={() => onFeedback(hiringRequest)}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
                  >
                    <MessageSquare className="h-4 w-4" />
                    Feedback
                  </button>
                )}

              {/* Revert */}
              {roles.includes("HR") &&
                hiringRequest.recruitment_request_status === "Approved" &&
                onRevert && (
                  <button
                    onClick={() => onRevert(hiringRequest.id)}
                    className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors"
                  >
                    <RotateCcw className="h-4 w-4" />
                    Revert
                  </button>
                )}

              {/* Convert to Campaign */}
              {hiringRequest.recruitment_request_status === "Approved" &&
                roles.includes("HR") &&
                onConvertToCampaign && (
                  <button
                    onClick={() => onConvertToCampaign(hiringRequest)}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Convert to Campaign
                  </button>
                )}
            </div>
          </div>
        )}

        {/* Timeline Footer */}
        <div className="border-t border-gray-200 pt-6">
          <div className="flex items-center justify-between text-sm text-gray-500">
            <div className="flex items-center space-x-4">
              <span>Created: {formatDate(hiringRequest.created_at)}</span>
              <span>•</span>
              <span>Updated: {formatDate(hiringRequest.updated_at)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HiringRequestDetails;
